import { useEffect, useState } from "react";

import {
  CheckIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import classNames from "../../../utils/classNames";
import usePartMutations from "../../../data/usePartMutations";
import useNotification from "../../notifications/useNotifications";
import { IPart } from "../../../models/Part";

const parseSuperseded = (raw: string | null | undefined): string[] =>
  (raw ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const serializeSuperseded = (list: string[]): string =>
  Array.from(new Set(list.map((s) => s.trim()).filter(Boolean))).join(", ");

export default function SupersededPartNumbersEditor({
  part,
}: {
  part: IPart;
}) {
  const { patchPart } = usePartMutations();
  const { addNotification } = useNotification();

  const [items, setItems] = useState<string[]>(() =>
    parseSuperseded(part.supersededPartNumbers)
  );
  const [input, setInput] = useState("");

  useEffect(() => {
    setItems(parseSuperseded(part.supersededPartNumbers));
  }, [part.supersededPartNumbers]);

  const commit = (next: string[]) => {
    const serialized = serializeSuperseded(next);
    const previous = serializeSuperseded(items);
    if (serialized === previous) return;
    patchPart.mutate(
      {
        id: part.id,
        supersededPartNumbers: serialized === "" ? null : serialized,
      },
      {
        onSuccess: () => {
          part.supersededPartNumbers = serialized === "" ? null : serialized;
        },
        onError: () => {
          addNotification({
            primaryText: "Failed to update superseded MPNs",
            variant: "error",
          });
        },
      }
    );
  };

  const handleAdd = () => {
    const value = input.trim();
    if (!value) return;
    if (items.some((i) => i.toLowerCase() === value.toLowerCase())) {
      setInput("");
      return;
    }
    const next = [...items, value];
    setItems(next);
    setInput("");
    commit(next);
  };

  const handleRemove = (idx: number) => {
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
    commit(next);
  };

  return (
    <div className="rounded-md border border-dashed border-gray-200 bg-gray-50/60 px-3 py-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Superseded MPNs
        </p>
        <span className="text-[11px] text-gray-400">
          {items.length} {items.length === 1 ? "entry" : "entries"}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {items.length === 0 && (
          <span className="text-xs italic text-gray-400">
            None recorded yet
          </span>
        )}
        {items.map((item, idx) => (
          <span
            key={`${item}-${idx}`}
            className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200"
          >
            <span className="font-mono">{item}</span>
            <button
              type="button"
              onClick={() => handleRemove(idx)}
              title="Remove"
              aria-label={`Remove ${item}`}
              className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder="Add a superseded MPN…"
          className="block w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-mono text-gray-900 placeholder:text-gray-400 placeholder:font-sans focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!input.trim() || patchPart.isLoading}
          title="Add"
          className={classNames(
            "inline-flex h-7 items-center gap-1 rounded-md border border-primary-200 bg-primary-50 px-2 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100",
            "disabled:cursor-not-allowed disabled:opacity-50"
          )}
        >
          {patchPart.isLoading ? (
            <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          Add
        </button>
      </div>
    </div>
  );
}
